import React, { useMemo, useState } from 'react';
import { Card, Typography, Input, Row, Col, Space, Tag, Select, Divider } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useGetStreamingQaqcPipeWeldingdata } from '../../../../hooks/genericHooks';
import type { QaqcPipeWeldingdata } from '../../../../types';
import { SearchOutlined } from '@ant-design/icons';
import StandardTable from '../../../../components/common/StandardTable';
import VirtualList from 'rc-virtual-list';
import Button from '../../../../components/common/Button';

const { Title } = Typography;

const QaqcPipeWeldingDataList: React.FC = () => {
  const { data, isLoading, error } = useGetStreamingQaqcPipeWeldingdata();
  const list: QaqcPipeWeldingdata[] = data?.data ?? [];
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<{
    fluid?: string[];
    section?: string[]; // area
    line_no?: string[];
    spool_id?: string[];
    revision_no?: string[];
    weld_category?: string[];
    pipesize_name?: string[];
    serviceclass_name?: string[];
    weldtype_name?: string[];
    invalidity?: string[];
    code_no?: string[];
    remarks?: string[];
  }>({});

  const cascadeOrder: (keyof QaqcPipeWeldingdata)[] = [
    'fluid',
    'section',
    'line_no',
    'spool_id',
    'revision_no',
    'weld_category',
    'pipesize_name',
    'serviceclass_name',
    'invalidity',
    'weldtype_name',
    'code_no',
    'remarks',
  ];

  // Virtualized tbody wrapper using rc-virtual-list
  const itemHeight = 40;
  const viewportHeight = 600;
  const VirtualBody = (rawData: any[], { children }: any) => {
    const rows = Array.isArray(children) ? children : (children ? [children] : []);
    return (
      <VirtualList
        data={rows}
        height={viewportHeight}
        itemHeight={itemHeight}
        itemKey={(child: any) => (child?.key as React.Key)}
        component="tbody"
      >
        {(child: any) => child}
      </VirtualList>
    );
  };

  const applyPartialFilters = (
    dataIn: QaqcPipeWeldingdata[],
    opts?: { excludeField?: keyof QaqcPipeWeldingdata }
  ) => {
    return dataIn.filter(row => {
      for (const k of cascadeOrder) {
        if (opts?.excludeField === k) continue;
        const vals = (filters as any)[k] as string[] | undefined;
        if (vals && vals.length > 0) {
          const cell = String((row as any)[k] ?? '');
          const cellLower = cell.toLowerCase();
          const matched = k === 'line_no'
            ? vals.some(v => String(v).toLowerCase().trim() !== '' && cellLower.includes(String(v).toLowerCase()))
            : vals.some(v => String(v).toLowerCase() === cellLower);
          if (!matched) return false;
        }
      }
      return true;
    });
  };

  const buildOptions = (field: keyof QaqcPipeWeldingdata) => {
    const s = search.trim().toLowerCase();
    const searchFiltered = s
      ? list.filter(r => {
          const hay = `${r.weldingdata_id} ${r.line_no}`.toLowerCase();
          return hay.includes(s);
        })
      : list;
    const base = applyPartialFilters(searchFiltered, { excludeField: field });
    const set = new Set<string>();
    base.forEach(r => {
      const v = (r as any)[field];
      if (v !== null && v !== undefined && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set).sort((a,b) => a.localeCompare(b)).map(v => ({ label: v, value: v }));
  };

  const filteredData = useMemo(() => {
    const s = search.trim().toLowerCase();
    const byFilters = applyPartialFilters(list);
    if (!s) return byFilters;
    return byFilters.filter((r: QaqcPipeWeldingdata) => {
      const hay = `${r.weldingdata_id} ${r.line_no}`.toLowerCase();
      return hay.includes(s);
    });
  }, [list, search, filters]);

  const columns: ColumnsType<QaqcPipeWeldingdata> = [
    { title: 'Welding Data ID', dataIndex: 'weldingdata_id', key: 'weldingdata_id', width:220},
    { title: 'Line No', dataIndex: 'line_no', key: 'line_no' },
    { title: 'Section', dataIndex: 'section', key: 'section', width: 80 },
    { title: 'Spool', dataIndex: 'spool_id', key: 'spool_id', width: 80 },
    { title: 'Fluid', dataIndex: 'fluid', key: 'fluid', width: 70},
    { title: 'Rev', dataIndex: 'revision_no', key: 'revision_no', width: 60 },
    { title: 'Weld No', dataIndex: 'weld_no', key: 'weld_no', width: 90 },
    { title: 'Cat', dataIndex: 'weld_category', key: 'weld_category', width: 50 },
    { title: 'Size', dataIndex: 'pipesize_name', key: 'pipesize_name', width: 70 },
    { title: 'W.Type', dataIndex: 'weldtype_name', key: 'weldtype_name', width: 80 },
    { title: 'S.Class', dataIndex: 'serviceclass_name', key: 'serviceclass_name', width: 90 },
    { title: 'Invalid', dataIndex: 'invalidity', key: 'invalidity', width: 60, render: (v) => (v ? <Tag color="red">{v}</Tag> : '-') },
    { title: 'Code', dataIndex: 'code_no', key: 'code_no', width: 70},
    { title: 'Remarks', dataIndex: 'remarks', key: 'remarks' },
  ];

  return (
    <Card>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Col><Title level={4} style={{ margin: 0 }}>QA/QC Piping • Welding Data</Title></Col>
        <Col>
          <Space>
            <Input
              allowClear
              placeholder="Search id/line"
              prefix={<SearchOutlined />}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{ width: 360 }}
            />
            <Button size="small" onClick={() => { setFilters({}); setSearch(''); }}>Reset</Button>
          </Space>
        </Col>
      </Row>

      <Space direction="vertical" style={{ width: '100%', marginBottom: 12 }} size={8}>
        <Row gutter={[8,8]}>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('fluid')}
              placeholder="Fluid"
              value={filters.fluid}
              onChange={(v) => setFilters(prev => ({ ...prev, fluid: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('section')}
              placeholder="Area (Section)"
              value={filters.section}
              onChange={(v) => setFilters(prev => ({ ...prev, section: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('line_no')}
              placeholder="Line No"
              value={filters.line_no}
              onChange={(v) => setFilters(prev => ({ ...prev, line_no: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              optionFilterProp="label"
              filterOption={(input, option) =>
                (String(option?.label ?? '').toLowerCase()).startsWith(input.toLowerCase())
              }
              options={buildOptions('spool_id')}
              placeholder="Spool"
              value={filters.spool_id}
              onChange={(v) => setFilters(prev => ({ ...prev, spool_id: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('revision_no')}
              placeholder="Rev"
              value={filters.revision_no}
              onChange={(v) => setFilters(prev => ({ ...prev, revision_no: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('weld_category')}
              placeholder="W.Cat"
              value={filters.weld_category}
              onChange={(v) => setFilters(prev => ({ ...prev, weld_category: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('pipesize_name')}
              placeholder="Pipe Size"
              value={filters.pipesize_name}
              onChange={(v) => setFilters(prev => ({ ...prev, pipesize_name: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('serviceclass_name')}
              placeholder="S.Class"
              value={filters.serviceclass_name}
              onChange={(v) => setFilters(prev => ({ ...prev, serviceclass_name: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('invalidity')}
              placeholder="Invalid"
              value={filters.invalidity}
              onChange={(v) => setFilters(prev => ({ ...prev, invalidity: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('weldtype_name')}
              placeholder="W.Type"
              value={filters.weldtype_name}
              onChange={(v) => setFilters(prev => ({ ...prev, weldtype_name: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('code_no')}
              placeholder="Code"
              value={filters.code_no}
              onChange={(v) => setFilters(prev => ({ ...prev, code_no: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={2}>
            <Select
              mode="multiple"
              allowClear
              showSearch
              options={buildOptions('remarks')}
              placeholder="Remarks"
              value={filters.remarks}
              onChange={(v) => setFilters(prev => ({ ...prev, remarks: (v && v.length ? v : undefined) }))}
              style={{ width: '100%' }}
            />
          </Col>
        </Row>
      </Space>

      <StandardTable<QaqcPipeWeldingdata>
        loading={isLoading}
        dataSource={filteredData}
        columns={columns as any}
        rowKey="id"
        bordered
        pagination={{ pageSize: 20, showSizeChanger: true, pageSizeOptions: ['10','20','50','100'] }}
        components={{ body: VirtualBody as any }}
      />
    </Card>
  );
};

export default QaqcPipeWeldingDataList;
